/**
 * MenuBuilderShell — scope-agnostic szkielet edytora menu (ADR-0031 / ADR-0034).
 *
 * Wyciągnięty z `apps/admin/src/pages/menu/MenuBuilderPage.tsx` żeby admin scope
 * (ADR-0034) mógł reużyć tę samą UX bez duplikatu ~700 LOC. Cała logika stanu,
 * mutacje, modal handling, render — żyje tutaj. Wrappery (TenantMenuBuilderPage,
 * AdminMenuBuilderPage) tylko wstrzykują:
 *   - apiPrefix (`/menu` vs `/admin/menu`)
 *   - i18nPrefix (`tenant.menuLayoutBuilder` vs `admin.menuLayoutBuilder`)
 *   - queryKeys (cache namespacing tenant vs admin)
 *   - useLayoutsHook / useSourceHook / useAssignmentsHook (scope-specific data fetch)
 *   - rolesQuery (już adaptowany do RoleOption[] przez caller)
 *   - systemItemDefinitions (SYSTEM_ITEMS vs ADMIN_SYSTEM_ITEMS)
 *   - allowModuleType (true=tenant ma moduły, false=admin nie ma)
 *   - backUrl (gdzie Wstecz nawigować)
 *
 * Tenant 1064 RTL testów pozostaje GREEN bez modyfikacji asercji — wrapper
 * `MenuBuilderPage` zachowuje identyczne zachowanie przez sensowne defaulty.
 */

import { type ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  AdminMenuItem,
  AdminMenuSourceResponse,
  AdminSystemItemDefinition,
  MenuItem,
  MenuLayoutDto,
  MenuLayoutSummary,
  MenuSourceResponse,
  ModuleTemplateEntityDefinition,
  SystemItemDefinition,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  Eye,
  Pencil,
  Plus,
  Save,
  Star,
  Trash2,
  Users,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CollapsedColumnRail, ColumnResizer } from '@/components/menu/ColumnControls';
import {
  LayoutNameModal,
  type LayoutNameModalMode,
  type LayoutSettingsValues,
} from '@/components/menu/LayoutNameModal';
import {
  type AssignmentsQueryLike,
  LayoutRolesModal,
  type RolesQueryLike,
} from '@/components/menu/LayoutRolesModal';
import { MenuCanvas } from '@/components/menu/MenuCanvas';
import { MenuItemPropertiesPanel } from '@/components/menu/MenuItemPropertiesPanel';
import { MenuPalette } from '@/components/menu/MenuPalette';
import { MenuPreviewSidebar } from '@/components/menu/MenuPreviewSidebar';
import { type MenuNodePreviewData, MenuUnifiedPreview } from '@/components/menu/MenuUnifiedPreview';
import { TenantKartotekaFormBuilderModal } from '@/components/menu/TenantKartotekaFormBuilderModal';
import { TenantKartotekaListBuilderModal } from '@/components/menu/TenantKartotekaListBuilderModal';
import {
  type GalleryModuleMenu,
  type GalleryView,
  MenuLayoutGallery,
} from '@/components/menu-builder-shell/MenuLayoutGallery';
import { MenuListPane } from '@/components/menu-builder-shell/MenuListPane';
import { PreviewModal } from '@/components/preview/PreviewModal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { type MenuColor, parseMenuColor } from '@/lib/menu-colors';
import { collectUsedPaletteKeys } from '@/lib/menu-dnd';
import {
  findInTree,
  insertIntoContainer,
  menuItemsEqual,
  moveInTree,
  removeFromTree,
  reorderInContainer,
  updateInTree,
} from '@/lib/menu-tree';
import type { TenantKartotekaFormBuilderData } from '@/lib/preview/tenant-menu-preview';

interface LayoutResponse<T extends MenuLayoutDto = MenuLayoutDto> {
  layout: T;
}

interface LayoutsListResponse<T extends MenuLayoutSummary = MenuLayoutSummary> {
  layouts: T[];
}

/**
 * Minimum-shape dla system item lookup (label fallback w MenuItemBlock).
 * Wystarcza dla tenant SystemItemDefinition + admin AdminSystemItemDefinition.
 */
type AnySystemItemDefinition = Pick<SystemItemDefinition, 'labelI18nKey' | 'defaultLabel'> &
  Partial<Pick<SystemItemDefinition, 'iconName' | 'path'>>;

export interface MenuBuilderShellQueryKeys {
  /** Cache key dla aktywnego layoutu (used przez sidebar custom render). Invalidate po mutacjach. */
  activeLayout: readonly unknown[];
  /** Cache key dla listy layoutów (dropdown selektor). Invalidate po create/rename/delete/role assignments. */
  layoutsList: readonly unknown[];
  /** Cache key dla available layouts (LayoutSwitcherFooter — które user może wybrać). Invalidate po role assignments. */
  availableLayouts: readonly unknown[];
  /** Cache key dla pełnego layoutu (z hierarchy) — factory bo zależny od id. */
  layoutById: (id: string | null) => readonly unknown[];
  /** Cache key dla role assignments — factory. Opcjonalne: tylko scope z „Przypisz do ról" (admin). */
  roleAssignments?: (id: string | null) => readonly unknown[];
}

export interface MenuBuilderShellProps {
  /** Base path dla API requestów. Tenant: `/menu`. Admin: `/admin/menu`. */
  apiPrefix: string;
  /** i18n key prefix. Tenant: `tenant.menuLayoutBuilder`. Admin: `admin.menuLayoutBuilder`. */
  i18nPrefix: string;
  /** Cache keys — caller manages namespacing tenant vs admin. */
  queryKeys: MenuBuilderShellQueryKeys;
  /** URL do nawigacji „Wstecz". Tenant: `/settings?tab=menu-builder`. Admin: `/dashboard`. */
  backUrl: string;
  /** Czy paletka pokazuje sekcje `modules` + `customModules`. Tenant: true. Admin: false. */
  allowModuleType: boolean;
  /** Hook pobierający listę layoutów (dropdown). */
  useLayoutsHook: () => UseQueryResult<LayoutsListResponse>;
  /**
   * Hook pobierający źródło paletki. Tenant zwraca `MenuSourceResponse`
   * (modules + customModules + systemItems), admin zwraca `AdminMenuSourceResponse`
   * (tylko systemItems). MenuPalette obsługuje oba przez 'modules' in source guards.
   */
  useSourceHook: () => UseQueryResult<MenuSourceResponse | AdminMenuSourceResponse>;
  /**
   * Hook pobierający role assignments per layoutId (modal „Przypisz do ról").
   * OPCJONALNE: gdy nie podane (Kreator menu TENANTA — menu przypisuje się per user/rola/zespół
   * w Centrum uprawnień, F24), przycisk „Przypisz do ról" jest ukryty. Admin scope wciąż podaje.
   */
  useAssignmentsHook?: (layoutId: string | null) => AssignmentsQueryLike;
  /** Dostępne role do wyboru w modalu (adaptowane przez caller na RoleOption[]). Opcjonalne — patrz wyżej. */
  rolesQuery?: RolesQueryLike;
  /** Lookup system item definicji (label fallback w MenuItemBlock + paletka). */
  systemItemDefinitions: ReadonlyMap<string, AnySystemItemDefinition | AdminSystemItemDefinition>;
  /**
   * Tenant (makieta v2, 2026-07-02): menu modułów do sekcji read-only galerii.
   * `hierarchy` = fragment przełożony na MenuItem[] (convertInstalledItemsToMenuItems)
   * — zasila Podgląd i Duplikuj (kopia → własny edytowalny układ). Admin nie podaje.
   */
  moduleMenus?: Array<GalleryModuleMenu & { hierarchy: MenuItem[] }>;
  /**
   * Układ strony (makieta Eugenii 2026-07-02, wzór z edytora szablonu huba):
   *  - 'gallery' (default, admin/ITE): galeria pełnoekranowa ↔ edytor.
   *  - 'list' (tenant): jeden widok — wąska lista po lewej („Menu systemowe"
   *    read-only z podglądem/duplikatem + „Menu customowe" z „+" i akcjami
   *    per wiersz), edytor wybranego menu po prawej.
   */
  paneMode?: 'gallery' | 'list';
  /**
   * Klasa wysokości głównego kontenera (edytor, lista i galeria). Domyślnie `h-full` —
   * host musi dać rodzica o określonej wysokości (tenant: panel karty `absolute inset-0`).
   * Host bez takiego rodzica (panel ITE: `<main>` Layoutu ma wysokość auto) podaje jawną,
   * np. `h-[calc(100vh-6.5rem)]` — wzór z TemplateMenuBuilderPage.
   */
  heightClassName?: string;
  /**
   * Resolver danych zunifikowanego podglądu (tenant: `useTenantMenuPreviewResolver`).
   * Gdy podany — podgląd menu systemowego i „Podgląd menu" edytora otwierają PEŁNY
   * `MenuUnifiedPreview` (klikalna makieta + lista/formularz na próbkach, jak w hubie).
   * Bez niego (admin) — dotychczasowy modal z samą makietą sidebara.
   */
  resolvePreviewNode?: (node: MenuItem) => MenuNodePreviewData | null;
  /**
   * Akordeon pozycji jak w hubie (tenant, feedback 2026-07-02): klik pozycji w
   * canvasie rozwija pod wierszem sekcje Ustawienia / Layout formularza / Widok
   * list / Filtry stałe (MenuItemInlineEditor) zamiast prawego panelu właściwości.
   * `entities` = syntetyczne encje z kartotek tenanta (useTenantKartotekaViews).
   * Tylko tryb 'list'; admin (gallery) zostaje przy panelu właściwości.
   */
  kartotekaViews?: {
    entities: ModuleTemplateEntityDefinition[];
    resolveEntitySlug: (item: MenuItem | AdminMenuItem) => string | null;
  };
  /**
   * Tworzenie kartoteki własnej z palety (wariant A, 2026-07-03): handler strony
   * tenanta (POST /modules + invalidacja źródła palety). Podany tylko gdy user ma
   * `crm_core.custom_modules.manage` — bez propa paleta nie pokazuje „+".
   */
  onCreateKartoteka?: (name: string) => Promise<{ slug: string; label: string }>;
  /**
   * Mutacje layoutów kartotek z akordeonu (etap b cz.2, 2026-07-03): „+ Nowy" /
   * „Duplikuj" / „Usuń" w sekcjach Layout formularza i Widok list. Tenant podaje
   * `useTenantKartotekaLayoutActions`; bez propa (admin) przyciski się nie renderują.
   * Kształt strukturalny (bez importu tenant lib — shell zostaje scope-agnostyczny).
   */
  kartotekaLayoutActions?: {
    canMutate: (entitySlug: string) => boolean;
    createLayout: (entitySlug: string, type: 'form' | 'list', name: string) => Promise<string>;
    duplicateLayout: (
      entitySlug: string,
      type: 'form' | 'list',
      sourceLayoutId: string | null,
      name: string,
    ) => Promise<string>;
    deleteLayout: (entitySlug: string, layoutId: string) => Promise<void>;
    renameLayout: (entitySlug: string, layoutId: string, name: string) => Promise<void>;
    getFormBuilderData: (
      entitySlug: string,
      layoutId: string | null,
    ) => TenantKartotekaFormBuilderData | null;
    saveFormLayout: (
      entitySlug: string,
      layoutId: string,
      builderTree: Record<string, unknown>,
    ) => Promise<void>;
    getListBuilderTarget: (
      entitySlug: string,
      layoutId: string | null,
    ) => {
      scope: 'module' | 'system';
      code: string;
      forcedLayoutId: string | null;
      entityName: string;
      layoutName: string | null;
    } | null;
    invalidateKartoteka: (entitySlug: string) => Promise<void>;
  };
  /**
   * Włącza przeciąganie elementów palety na płótno (natywny drag & drop, 2026-09-30).
   * Tenant podaje true; ITE (AdminMenuBuilderPage) zostaje przy samych plusach (default false).
   */
  enablePaletteDnd?: boolean;
  /**
   * Eksport/import menu do pliku (przenoszenie menu między tenantami). Tylko scope tenanta
   * i tylko z prawem edycji menu — brak = przyciski ukryte (panel ITE). Shell trzyma stan okna
   * importu i po zapisie otwiera nowe menu tą samą drogą co klik wiersza (z ochroną zmian).
   */
  menuTransfer?: {
    onExport: (layoutId: string) => void;
    ImportModal: ComponentType<{
      open: boolean;
      onClose: () => void;
      onImported: (layoutId: string) => void;
      /** Podgląd menu z pliku przed zapisem — shell otwiera swoje okno podglądu menu. */
      onPreview: (menu: { name: string; hierarchy: MenuItem[] }) => void;
    }>;
  };
}

/** No-op zastępnik `useAssignmentsHook`, gdy funkcja „Przypisz do ról" jest wyłączona (tenant). */
function useNoopAssignments(_layoutId: string | null): AssignmentsQueryLike {
  return { data: undefined, isLoading: false };
}

export function MenuBuilderShell({
  apiPrefix,
  i18nPrefix,
  queryKeys,
  backUrl,
  allowModuleType,
  useLayoutsHook,
  useSourceHook,
  useAssignmentsHook,
  rolesQuery,
  systemItemDefinitions,
  moduleMenus,
  paneMode = 'gallery',
  heightClassName = 'h-full',
  resolvePreviewNode,
  kartotekaViews,
  onCreateKartoteka,
  kartotekaLayoutActions,
  enablePaletteDnd = false,
  menuTransfer,
}: MenuBuilderShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // ───── server queries ────────────────────────────────────────────────────

  const layoutsQuery = useLayoutsHook();
  const sourceQuery = useSourceHook();

  // ───── state ──────────────────────────────────────────────────────────────

  const [currentLayoutId, setCurrentLayoutId] = useState<string | null>(null);
  // Hierarchy state jest świadomie unionowy `MenuItem | AdminMenuItem` — shell obsługuje
  // oba scope'y bez duplikatu kodu. Helpery `menu-tree` (findInTree, insertIntoContainer,
  // updateInTree, removeFromTree, reorderInContainer, menuItemsEqual) są typowane tylko
  // na `MenuItem[]`, stąd cast'y `as MenuItem[]` przy każdym wywołaniu. Runtime safe bo
  // oba typy są strukturalnie kompatybilne dla operacji helperów (id, type, children).
  // Pełna generalizacja helperów na `<T extends BaseMenuItem>` świadomie odłożona poza
  // shell extraction (ruszyłaby testy menu-tree + ukryte typing assumptions).
  const [hierarchy, setHierarchy] = useState<Array<MenuItem | AdminMenuItem>>([]);
  const [currentVersion, setCurrentVersion] = useState<number>(1);
  const [currentName, setCurrentName] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Paleta widoczna domyślnie; przycisk w nagłówku ją zwija/rozwija. Zwinięta = drzewo
  // dostaje pełną szerokość (mniej kolumn). Domyślnie otwarta, żeby afordancja dodawania
  // pozycji była od razu widoczna.
  const [paletteOpen, setPaletteOpen] = useState(true);
  // Ręczne poszerzanie palety (uchwyt na krawędzi) — szerokość zapamiętana w localStorage.
  const [paletteWidth, setPaletteWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 256;
    const n = Number(window.localStorage.getItem('menuBuilder.paletteWidth'));
    return Number.isFinite(n) && n > 0 ? Math.min(560, Math.max(200, n)) : 256;
  });
  useEffect(() => {
    window.localStorage.setItem('menuBuilder.paletteWidth', String(paletteWidth));
  }, [paletteWidth]);
  // Kolumna listy menu (pierwsza) — również zwijalna + poszerzalna, ten sam wzorzec.
  const [menuListOpen, setMenuListOpen] = useState(true);
  const [menuListWidth, setMenuListWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 288;
    const n = Number(window.localStorage.getItem('menuBuilder.menuListWidth'));
    return Number.isFinite(n) && n > 0 ? Math.min(560, Math.max(220, n)) : 288;
  });
  useEffect(() => {
    window.localStorage.setItem('menuBuilder.menuListWidth', String(menuListWidth));
  }, [menuListWidth]);

  // Modal state.
  const [nameModalMode, setNameModalMode] = useState<LayoutNameModalMode | null>(null);
  const [nameModalServerError, setNameModalServerError] = useState<string | null>(null);
  // Menu, którego nazwę/ikonę zmienia modal 'rename' otwarty z wiersza listy (null = otwarte w edytorze).
  const [renameTargetId, setRenameTargetId] = useState<string | null>(null);
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [rolesModalServerError, setRolesModalServerError] = useState<string | null>(null);

  // ───── assignments query (modal scope) ───────────────────────────────────
  // Wstrzykiwany hook — tenant useLayoutRoleAssignments albo admin equivalent.
  // Pobiera tylko gdy modal otwarty + layout wybrany (enabled w hooku).
  // Funkcja „Przypisz do ról" jest opcjonalna (ukryta w Kreatorze menu tenanta — F24).
  // Hook wołamy ZAWSZE dokładnie raz (rules-of-hooks); gdy caller nie podał, używamy no-op.
  // Prop jest stabilny per zamontowany caller (tenant nigdy nie podaje, admin zawsze), więc
  // implementacja hooka nie zmienia się w trakcie życia komponentu.
  const roleAssignmentsEnabled = Boolean(useAssignmentsHook);
  const useAssignmentsHookResolved = useAssignmentsHook ?? useNoopAssignments;
  const assignmentsQuery = useAssignmentsHookResolved(rolesModalOpen ? currentLayoutId : null);

  // Confirm dialogs.
  const [confirmBack, setConfirmBack] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingSwitchTo, setPendingSwitchTo] = useState<string | null>(null);

  // Akcje galerii (makieta v2): duplikat/usunięcie per wiersz + duplikat/podgląd
  // menu modułowego. `galleryCloneFromId` steruje źródłem klonu w modalu nazwy
  // (bez niego klonem jest layout otwarty w edytorze — stare zachowanie).
  const [galleryCloneFromId, setGalleryCloneFromId] = useState<string | null>(null);
  const [galleryDeleteId, setGalleryDeleteId] = useState<string | null>(null);
  const [pendingModuleClone, setPendingModuleClone] = useState<string | null>(null);
  const [previewModuleCode, setPreviewModuleCode] = useState<string | null>(null);
  // Podgląd menu z pliku importu (przed zapisem) — to samo okno co podgląd menu systemowego.
  const [importPreviewMenu, setImportPreviewMenu] = useState<{
    name: string;
    hierarchy: MenuItem[];
  } | null>(null);
  // Duplikat menu systemowego kliknięty przy niezapisanych zmianach w edytorze —
  // sukces duplikatu przełącza edytor na kopię, więc najpierw confirm.
  const [pendingModuleCloneConfirm, setPendingModuleCloneConfirm] = useState<string | null>(null);
  // Podgląd bieżącej (roboczej) hierarchii edytora — przycisk „Podgląd menu" (tryb 'list')
  // oraz wejścia per-pozycja z akordeonu (podgląd formularza/listy pozycji).
  const [previewCurrentOpen, setPreviewCurrentOpen] = useState(false);
  const [previewCurrentNodeId, setPreviewCurrentNodeId] = useState<string | null>(null);
  const [previewCurrentMode, setPreviewCurrentMode] = useState<'form' | 'list'>('list');

  // Dropdown open state.
  const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);

  // Widok: galeria zapisanych layoutów (ekran wejścia) vs edytor pojedynczego layoutu.
  const [view, setView] = useState<'gallery' | 'editor'>('gallery');
  // Tryb prezentacji galerii (lista/kafelki) — zapamiętany per scope w localStorage.
  const galleryViewStorageKey = `menuBuilderGalleryView:${apiPrefix}`;
  const [galleryView, setGalleryView] = useState<GalleryView>(() => {
    if (typeof window === 'undefined') return 'tiles';
    return window.localStorage.getItem(galleryViewStorageKey) === 'list' ? 'list' : 'tiles';
  });
  const changeGalleryView = (next: GalleryView) => {
    setGalleryView(next);
    try {
      window.localStorage.setItem(galleryViewStorageKey, next);
    } catch {
      /* prywatny tryb / brak storage — tryb zostaje tylko w pamięci sesji */
    }
  };

  // Wejście do edytora konkretnego layoutu z galerii.
  const openLayout = (id: string) => {
    setCurrentLayoutId(id);
    setView('editor');
  };

  // ───── initial layout pick (po fetch listy) ──────────────────────────────

  const initializedRef = useRef(false);
  useEffect(() => {
    if (!layoutsQuery.data || initializedRef.current) return;
    const list = layoutsQuery.data.layouts;
    if (list.length === 0) {
      initializedRef.current = true;
      return;
    }
    // Pierwszy raz — wybierz default, fallback do pierwszego z listy.
    const def = list.find((l) => l.isDefault) ?? list[0];
    if (def) {
      setCurrentLayoutId(def.id);
    }
    initializedRef.current = true;
  }, [layoutsQuery.data]);

  // ───── fetch wybranego layoutu (full z hierarchy) ────────────────────────

  const layoutQuery = useQuery<LayoutResponse>({
    queryKey: queryKeys.layoutById(currentLayoutId),
    queryFn: async () => {
      const res = await api.get<LayoutResponse>(`${apiPrefix}/layouts/${currentLayoutId}`);
      return res.data;
    },
    enabled: currentLayoutId !== null,
    staleTime: 30_000,
  });

  // Po pobraniu layoutu — załaduj hierarchy do state buildera.
  // Guard via ref: load JEDEN raz per layout id. Subsequent invalidate+refetch
  // tej samej tabeli nie nadpisuje local state (chroni przed wymazaniem zmian
  // po STALE_LAYOUT 409, gdzie state ustawiamy z `current` w body 409).
  // Switch na inny layout = nowy id → ref mismatch → load.
  const loadedLayoutIdRef = useRef<string | null>(null);
  // To samo co ref, ale jako stan — do flagi „niezapisane zmiany" (ref nie wywołuje renderu).
  const [loadedLayoutId, setLoadedLayoutId] = useState<string | null>(null);
  useEffect(() => {
    if (!layoutQuery.data) return;
    const layout = layoutQuery.data.layout;
    if (loadedLayoutIdRef.current === layout.id) return;
    setHierarchy(layout.hierarchy as Array<MenuItem | AdminMenuItem>);
    setCurrentVersion(layout.version);
    setCurrentName(layout.name);
    setSelectedId(null);
    loadedLayoutIdRef.current = layout.id;
    setLoadedLayoutId(layout.id);
  }, [layoutQuery.data]);

  // ───── dirty flag ────────────────────────────────────────────────────────

  const initialHierarchy = useMemo<Array<MenuItem | AdminMenuItem>>(
    () =>
      (layoutQuery.data?.layout?.hierarchy as Array<MenuItem | AdminMenuItem> | undefined) ?? [],
    [layoutQuery.data],
  );

  // Porównujemy dopiero, gdy w edytorze siedzi drzewo TEGO menu, które wskazuje zapytanie.
  // Przy przełączeniu menu przez jeden render `hierarchy` to jeszcze poprzednie menu, a
  // `initialHierarchy` już nowe — bez tej bramki migała plakietka „Niezapisane zmiany"
  // (i nagłówek edytora podskakiwał).
  const isDirty = useMemo(
    () =>
      loadedLayoutId !== null &&
      layoutQuery.data?.layout.id === loadedLayoutId &&
      !menuItemsEqual(hierarchy as MenuItem[], initialHierarchy as MenuItem[]),
    [hierarchy, initialHierarchy, loadedLayoutId, layoutQuery.data],
  );

  const selectedItem = useMemo<MenuItem | AdminMenuItem | null>(
    () =>
      selectedId
        ? (findInTree(hierarchy as MenuItem[], selectedId) as MenuItem | AdminMenuItem | null)
        : null,
    [hierarchy, selectedId],
  );

  // ───── canvas handlers ───────────────────────────────────────────────────

  // Nowo dodana pozycja zostaje ZWINIĘTA (feedback Eugenii 2026-09-30) — nie otwieramy
  // szczegółów LIŚCIA (kartoteka/moduł/link/separator); błysk na płótnie wystarcza.
  // WYJĄTEK: nowa sekcja zostaje zaznaczona, żeby (a) dało się ją od razu nazwać i
  // (b) kolejny „+" trafiał do jej wnętrza (nesting plusem to jedyna droga w panelu ITE,
  // gdzie DnD jest wyłączone). Przy liściu NIE ruszamy zaznaczenia — inaczej dodanie
  // liścia do zaznaczonej sekcji odznaczałoby ją i psuło dodawanie kolejnych pozycji.
  const selectIfSection = (item: MenuItem | AdminMenuItem) => {
    if (item.type === 'section') setSelectedId(item.id);
  };

  const handleAddItem = (newItem: MenuItem | AdminMenuItem) => {
    // Cast `newItem as MenuItem` jest bezpieczny w runtime: tree-helpers
    // (insertIntoContainer / findInTree / etc.) operują na strukturalnym kontrakcie
    // `{id, type, children?, ...}` który MenuItem i AdminMenuItem dzielą — różnią się
    // tylko enumami `type` (tenant ma `module`, admin nie) i `itemKey`. Helpers nie
    // robią narrowing po dyskryminantcie, tylko mutują arrays + przepuszczają shape.
    // Defense-in-depth: paletka admin (allowModuleType=false) nie generuje 'module',
    // więc admin scope nigdy nie podaje shape'u który byłby semantycznie różny.
    // Pełna generalizacja `lib/menu-tree.ts` na <T extends BaseMenuItem> = osobny task.
    const selected = selectedId ? findInTree(hierarchy as MenuItem[], selectedId) : null;
    if (selected && selected.type === 'section') {
      setHierarchy((h) => insertIntoContainer(h as MenuItem[], selected.id, newItem as MenuItem));
    } else {
      setHierarchy((h) => insertIntoContainer(h as MenuItem[], null, newItem as MenuItem));
    }
    selectIfSection(newItem);
  };

  // Dodanie pozycji z palety przez upuszczenie w konkretne miejsce (drag & drop).
  // containerId=null → top-level; index = pozycja wstawienia policzona przez płótno.
  const handleAddItemAt = (
    containerId: string | null,
    index: number,
    newItem: MenuItem | AdminMenuItem,
  ) => {
    setHierarchy((h) =>
      insertIntoContainer(h as MenuItem[], containerId, newItem as MenuItem, index),
    );
    selectIfSection(newItem);
  };

  // Tożsamości pozycji już w menu — do znacznika „w menu" w palecie.
  const usedPaletteKeys = useMemo(() => collectUsedPaletteKeys(hierarchy), [hierarchy]);

  const handleDeleteItem = (id: string) => {
    setHierarchy((h) => removeFromTree(h as MenuItem[], id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleUpdateItem = (next: MenuItem | AdminMenuItem) => {
    setHierarchy((h) => updateInTree(h as MenuItem[], next.id, () => next as MenuItem));
  };

  const handleReorder = (parentId: string | null, fromIndex: number, toIndex: number) => {
    setHierarchy((h) => reorderInContainer(h as MenuItem[], parentId, fromIndex, toIndex));
  };

  // Przeniesienie uchwytem między poziomami (główny ↔ sekcja).
  const handleMove = (id: string, containerId: string | null, index: number) => {
    setHierarchy((h) => moveInTree(h as MenuItem[], id, containerId, index));
  };

  // ───── mutations ──────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!currentLayoutId) throw new Error('no layout selected');
      const res = await api.patch<LayoutResponse>(`${apiPrefix}/layouts/${currentLayoutId}`, {
        hierarchy,
        expectedVersion: currentVersion,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setCurrentVersion(data.layout.version);
      loadedLayoutIdRef.current = data.layout.id;
      queryClient.invalidateQueries({ queryKey: queryKeys.activeLayout });
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutById(currentLayoutId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts });
      toast.show({ variant: 'success', title: t(`${i18nPrefix}.layouts.toasts.saved`) });
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: { status?: number; data?: { error?: string; current?: MenuLayoutDto } };
      };
      if (e.response?.status === 409 && e.response.data?.error === 'STALE_LAYOUT') {
        toast.show({
          variant: 'warning',
          title: t(`${i18nPrefix}.layouts.conflict.staleLayout`),
        });
        const current = e.response.data.current;
        if (current) {
          setHierarchy(current.hierarchy as Array<MenuItem | AdminMenuItem>);
          setCurrentVersion(current.version);
          setCurrentName(current.name);
          // Sync query cache z server snapshot — bez tego `initialHierarchy`
          // (memo z layoutQuery.data) trzyma stary stan, więc isDirty=true
          // mimo że lokalny stan = świeży server state.
          queryClient.setQueryData(queryKeys.layoutById(currentLayoutId), {
            layout: current,
          });
          queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
        }
        return;
      }
      toast.show({
        variant: 'error',
        title: t(`${i18nPrefix}.saveErrorGeneric`),
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (input: {
      name: string;
      cloneFromId?: string;
      icon?: string | null;
      color?: MenuColor | null;
      isActive?: boolean;
    }) => {
      const res = await api.post<LayoutResponse>(`${apiPrefix}/layouts`, input);
      return res.data;
    },
    onSuccess: (data) => {
      setNameModalMode(null);
      setNameModalServerError(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts });
      // Nowy layout otwieramy od razu w edytorze (działa też gdy „Nowy" kliknięto z galerii).
      setCurrentLayoutId(data.layout.id);
      setView('editor');
      toast.show({
        variant: 'success',
        title: t(`${i18nPrefix}.layouts.toasts.created`),
      });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status === 409 && e.response.data?.error === 'NAME_TAKEN') {
        setNameModalServerError(t(`${i18nPrefix}.layouts.errors.nameTaken`));
        return;
      }
      setNameModalServerError(t(`${i18nPrefix}.layouts.errors.createFailed`));
    },
  });

  const renameMutation = useMutation({
    // Parametryzowane id+version — rename działa i z toolbara edytora (layout
    // otwarty), i z wiersza listy (tryb 'list', wersja z summary listy).
    mutationFn: async (input: {
      id: string;
      name: string;
      expectedVersion: number;
      /** undefined = nie zmieniaj; null = wartość domyślna. */
      icon?: string | null;
      color?: MenuColor | null;
      isActive?: boolean;
    }) => {
      const res = await api.patch<LayoutResponse>(`${apiPrefix}/layouts/${input.id}`, {
        name: input.name,
        ...(input.icon !== undefined ? { icon: input.icon } : {}),
        ...(input.color !== undefined ? { color: input.color } : {}),
        ...(input.isActive !== undefined ? { isActive: input.isActive } : {}),
        expectedVersion: input.expectedVersion,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setNameModalMode(null);
      setNameModalServerError(null);
      setRenameTargetId(null);
      if (data.layout.id === currentLayoutId) {
        setCurrentVersion(data.layout.version);
        setCurrentName(data.layout.name);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutById(data.layout.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts });
      toast.show({
        variant: 'success',
        title: t(`${i18nPrefix}.layouts.toasts.renamed`),
      });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      const message =
        e.response?.status === 409 && e.response.data?.error === 'NAME_TAKEN'
          ? t(`${i18nPrefix}.layouts.errors.nameTaken`)
          : e.response?.status === 409 && e.response.data?.error === 'STALE_LAYOUT'
            ? t(`${i18nPrefix}.layouts.conflict.staleLayout`)
            : t(`${i18nPrefix}.saveErrorGeneric`);
      // Rename z modalu (tryb 'gallery') → błąd inline w modalu; rename inline
      // z wiersza listy (tryb 'list', modal zamknięty) → toast.
      if (nameModalMode === 'rename') {
        setNameModalServerError(message);
      } else {
        toast.show({ variant: 'error', title: message });
      }
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      if (!currentLayoutId) throw new Error('no layout selected');
      const res = await api.post(`${apiPrefix}/layouts/${currentLayoutId}/set-default`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeLayout });
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutById(currentLayoutId) });
      toast.show({
        variant: 'success',
        title: t(`${i18nPrefix}.layouts.toasts.setAsDefault`),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t(`${i18nPrefix}.layouts.errors.setDefaultFailed`),
      });
    },
  });

  // Aktywuj / Dezaktywuj otwarte menu — od razu (PATCH samego isActive), bez „Zapisz": to nie jest
  // zmiana zawartości menu. Nieaktywne menu znika z paska bocznego, ale zostaje w kreatorze.
  const setActiveMutation = useMutation({
    mutationFn: async (nextActive: boolean) => {
      if (!currentLayoutId) throw new Error('no layout selected');
      const res = await api.patch<LayoutResponse>(`${apiPrefix}/layouts/${currentLayoutId}`, {
        isActive: nextActive,
        expectedVersion: currentVersion,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setCurrentVersion(data.layout.version);
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutById(data.layout.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeLayout });
      toast.show({
        variant: 'success',
        title: t(
          data.layout.isActive === false
            ? `${i18nPrefix}.layouts.toasts.deactivated`
            : `${i18nPrefix}.layouts.toasts.activated`,
        ),
      });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      const code = e.response?.data?.error;
      toast.show({
        variant: 'error',
        title:
          code === 'DEFAULT_CANNOT_BE_INACTIVE'
            ? t(`${i18nPrefix}.layouts.errors.defaultCannotBeInactive`)
            : code === 'STALE_LAYOUT'
              ? t(`${i18nPrefix}.layouts.conflict.staleLayout`)
              : t(`${i18nPrefix}.saveErrorGeneric`),
      });
    },
  });

  const updateRolesMutation = useMutation({
    mutationFn: async (roleIds: string[]) => {
      // Guard: „Przypisz do ról" istnieje tylko w scope z włączoną funkcją (admin). U tenanta trasa
      // została usunięta (F24) — nie wysyłamy żądania, które trafiłoby w 404.
      if (!roleAssignmentsEnabled) throw new Error('role assignments disabled in this scope');
      if (!currentLayoutId) throw new Error('no layout selected');
      const res = await api.put(`${apiPrefix}/layouts/${currentLayoutId}/role-assignments`, {
        roleIds,
        expectedVersion: currentVersion,
      });
      return res.data as { roleIds: string[]; version: number };
    },
    onSuccess: (data) => {
      setRolesModalOpen(false);
      setRolesModalServerError(null);
      setCurrentVersion(data.version);
      if (queryKeys.roleAssignments) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.roleAssignments(currentLayoutId),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeLayout });
      toast.show({
        variant: 'success',
        title: t(`${i18nPrefix}.layouts.toasts.rolesUpdated`),
      });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status === 409 && e.response.data?.error === 'STALE_LAYOUT') {
        setRolesModalServerError(t(`${i18nPrefix}.layouts.conflict.staleLayout`));
        return;
      }
      setRolesModalServerError(t(`${i18nPrefix}.saveErrorGeneric`));
    },
  });

  const deleteMutation = useMutation({
    // Parametryzowane id — usuwanie działa i z edytora (layout otwarty), i z galerii.
    mutationFn: async (id: string) => {
      const res = await api.delete(`${apiPrefix}/layouts/${id}`);
      return res.data;
    },
    onSuccess: (_data, id) => {
      setConfirmDelete(false);
      setGalleryDeleteId(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeLayout });
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts });
      if (id === currentLayoutId) {
        const list = layoutsQuery.data?.layouts ?? [];
        const next = list.find((l) => l.id !== id);
        setCurrentLayoutId(next?.id ?? null);
        // Tryb 'list': nie auto-otwieraj kolejnego menu (mogłoby wyciągnąć ukryty
        // pusty „Default") — wróć do placeholdera „Wybierz menu do edycji".
        if (paneMode === 'list') setView('gallery');
      }
      toast.show({
        variant: 'success',
        title: t(`${i18nPrefix}.layouts.toasts.deleted`),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t(`${i18nPrefix}.layouts.errors.deleteFailed`),
      });
    },
  });

  // Duplikat menu MODUŁOWEGO (galeria tenanta): create pustego layoutu + PATCH
  // hierarchii z kopii fragmentu, potem od razu edytor kopii (decyzja Eugenii).
  const duplicateModuleMutation = useMutation({
    mutationFn: async (input: {
      name: string;
      icon: string | null;
      color: MenuColor | null;
      isActive: boolean;
      hierarchy: MenuItem[];
    }) => {
      const created = await api.post<LayoutResponse>(`${apiPrefix}/layouts`, {
        name: input.name,
        icon: input.icon,
        color: input.color,
        isActive: input.isActive,
      });
      const patched = await api.patch<LayoutResponse>(
        `${apiPrefix}/layouts/${created.data.layout.id}`,
        { hierarchy: input.hierarchy, expectedVersion: created.data.layout.version },
      );
      return patched.data;
    },
    onSuccess: (data) => {
      setNameModalMode(null);
      setNameModalServerError(null);
      setPendingModuleClone(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList });
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts });
      setCurrentLayoutId(data.layout.id);
      setView('editor');
      toast.show({
        variant: 'success',
        title: t(`${i18nPrefix}.layouts.toasts.created`),
      });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status === 409 && e.response.data?.error === 'NAME_TAKEN') {
        setNameModalServerError(t(`${i18nPrefix}.layouts.errors.nameTaken`));
        return;
      }
      setNameModalServerError(t(`${i18nPrefix}.layouts.errors.createFailed`));
    },
  });

  // ───── topbar handlers ──────────────────────────────────────────────────

  const handleSwitchLayout = (id: string) => {
    setLayoutDropdownOpen(false);
    if (id === currentLayoutId) return;
    if (isDirty) {
      setPendingSwitchTo(id);
      return;
    }
    setCurrentLayoutId(id);
  };

  const confirmSwitch = () => {
    if (pendingSwitchTo) {
      setCurrentLayoutId(pendingSwitchTo);
      setPendingSwitchTo(null);
      // Tryb 'list': switch mógł przyjść z wiersza przy zamkniętym edytorze.
      setView('editor');
    }
  };

  // Okno importu menu z pliku (tylko gdy strona podała `menuTransfer`). Po zapisie nowe menu
  // otwieramy tą samą drogą co klik wiersza (ochrona niezapisanych zmian).
  const [importOpen, setImportOpen] = useState(false);
  const handleMenuImported = async (layoutId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.layoutsList }),
      queryClient.invalidateQueries({ queryKey: queryKeys.availableLayouts }),
    ]);
    openCustomLayout(layoutId);
  };

  // Otwarcie własnego menu z listy (tryb 'list') — dirty guard jak przy dropdownie.
  const openCustomLayout = (id: string) => {
    if (view === 'editor' && id === currentLayoutId) return;
    if (view === 'editor' && isDirty) {
      setPendingSwitchTo(id);
      return;
    }
    setCurrentLayoutId(id);
    setView('editor');
  };

  // „Wstecz": tryb 'gallery' wraca z edytora do galerii; tryb 'list' wychodzi ze
  // strony (backUrl) — lista i edytor żyją w jednym widoku. Zawsze z dirty guardem.
  const handleBack = () => {
    if (isDirty) {
      setConfirmBack(true);
    } else if (paneMode === 'list') {
      navigate(backUrl);
    } else {
      setView('gallery');
    }
  };

  const confirmBackAction = () => {
    setConfirmBack(false);
    if (paneMode === 'list') {
      navigate(backUrl);
    } else {
      setView('gallery');
    }
  };

  const openNameModal = (mode: LayoutNameModalMode) => {
    setNameModalServerError(null);
    setNameModalMode(mode);
  };

  const handleNameSubmit = (values: LayoutSettingsValues) => {
    const { name, icon, color, isActive } = values;
    if (nameModalMode === 'create') {
      createMutation.mutate({ name, icon, color, isActive });
    } else if (nameModalMode === 'duplicate') {
      // Trzy źródła duplikatu: menu modułowe (galeria tenanta) → create+patch kopii
      // fragmentu; wiersz galerii → clone wskazanego layoutu; edytor → clone otwartego.
      if (pendingModuleClone) {
        const moduleMenu = (moduleMenus ?? []).find((m) => m.code === pendingModuleClone);
        if (moduleMenu) {
          duplicateModuleMutation.mutate({
            name,
            icon,
            color,
            isActive,
            hierarchy: moduleMenu.hierarchy,
          });
        }
        return;
      }
      createMutation.mutate({
        name,
        icon,
        color,
        isActive,
        cloneFromId: galleryCloneFromId ?? currentLayoutId ?? undefined,
      });
    } else if (nameModalMode === 'rename') {
      // Modal „Edytuj menu": z wiersza listy (ołówek) dotyczy wskazanego menu, z toolbara edytora
      // (tryb 'gallery') — otwartego layoutu. Wersja OCC z summary listy, chyba że to layout
      // otwarty w edytorze — wtedy lokalna (najświeższa).
      const targetId = renameTargetId ?? currentLayoutId;
      if (!targetId) return;
      const summaryVersion =
        (layoutsQuery.data?.layouts ?? []).find((l) => l.id === targetId)?.version ?? 1;
      renameMutation.mutate({
        id: targetId,
        name,
        icon,
        color,
        isActive,
        expectedVersion: targetId === currentLayoutId ? currentVersion : summaryVersion,
      });
    }
  };

  // Ołówek na wierszu listy → modal ustawień (nazwa, ikona, kolor, aktywność) wskazanego menu.
  const handleEditCustom = (id: string) => {
    setRenameTargetId(id);
    openNameModal('rename');
  };

  // Wartości początkowe modalu: menu edytowane (rename) albo źródło klonu (duplicate).
  const allLayouts = layoutsQuery.data?.layouts ?? [];
  const renameTarget = renameTargetId ? allLayouts.find((l) => l.id === renameTargetId) : undefined;
  const moduleCloneSource = pendingModuleClone
    ? (moduleMenus ?? []).find((m) => m.code === pendingModuleClone)
    : undefined;
  const layoutCloneSource = galleryCloneFromId
    ? allLayouts.find((l) => l.id === galleryCloneFromId)
    : undefined;
  const modalSourceLayout =
    nameModalMode === 'rename'
      ? (renameTarget ?? allLayouts.find((l) => l.id === currentLayoutId))
      : (layoutCloneSource ?? allLayouts.find((l) => l.id === currentLayoutId));
  // Nazwa źródła klonu w modalu „Duplikuj" — wiersz galerii/listy / menu modułowe / edytor.
  const duplicateSourceName = moduleCloneSource?.name ?? layoutCloneSource?.name;
  // Ikona źródła — menu modułowe niesie ikonę modułu, layout własną.
  const modalIcon = moduleCloneSource ? moduleCloneSource.icon : modalSourceLayout?.icon;
  const modalColor = moduleCloneSource ? null : parseMenuColor(modalSourceLayout?.color);

  const closeNameModal = () => {
    setRenameTargetId(null);
    setNameModalMode(null);
    setNameModalServerError(null);
    setGalleryCloneFromId(null);
    setPendingModuleClone(null);
  };

  // ───── render ────────────────────────────────────────────────────────────

  const layouts = layoutsQuery.data?.layouts ?? [];
  const currentLayout = layouts.find((l) => l.id === currentLayoutId);
  const isCurrentDefault = currentLayout?.isDefault ?? false;
  const hasLayouts = layouts.length > 0;

  // Chip stanu + przycisk Aktywuj/Dezaktywuj otwartego menu (widoczne dopiero, gdy menu jest zapisane).
  const currentActive = currentLayout?.isActive !== false;
  const activeControls = currentLayout ? (
    <>
      <Badge
        size="sm"
        tone={currentActive ? 'success' : 'neutral'}
        data-testid="layout-active-chip"
      >
        {currentActive
          ? t(`${i18nPrefix}.layouts.status.active`)
          : t(`${i18nPrefix}.layouts.status.inactive`)}
      </Badge>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => setActiveMutation.mutate(!currentActive)}
        disabled={setActiveMutation.isPending || (isCurrentDefault && currentActive)}
        title={
          isCurrentDefault && currentActive
            ? t(`${i18nPrefix}.layouts.nameModal.activeLockedHint`)
            : undefined
        }
        data-testid="layout-action-toggle-active"
      >
        {currentActive
          ? t(`${i18nPrefix}.layouts.actions.deactivate`)
          : t(`${i18nPrefix}.layouts.actions.activate`)}
      </Button>
    </>
  ) : null;

  // Podgląd: menu systemowe (klik wiersza) albo menu z pliku importu (przed zapisem).
  const previewModuleMenu: { name: string; hierarchy: MenuItem[] } | undefined =
    (previewModuleCode
      ? (moduleMenus ?? []).find((m) => m.code === previewModuleCode)
      : undefined) ??
    importPreviewMenu ??
    undefined;
  const closeMenuPreview = () => {
    setPreviewModuleCode(null);
    setImportPreviewMenu(null);
  };
  const galleryKartoteki =
    sourceQuery.data && 'kartoteki' in sourceQuery.data
      ? (sourceQuery.data.kartoteki ?? [])
      : undefined;
  // Metadane modułów dla makiety sidebara w zunifikowanym podglądzie (etykiety/ikony).
  const sourceModules =
    sourceQuery.data && 'modules' in sourceQuery.data ? sourceQuery.data.modules : [];

  // Duplikat menu modułowego — z galerii od razu modal nazwy; z szyny kontekstu
  // (edytor otwarty) najpierw confirm gdy są niezapisane zmiany, bo sukces
  // duplikatu przełącza edytor na kopię (lokalny stan przepada).
  const handleModuleMenuDuplicate = (code: string) => {
    if (view === 'editor' && isDirty) {
      setPendingModuleCloneConfirm(code);
      return;
    }
    setPendingModuleClone(code);
    openNameModal('duplicate');
  };

  // Modale wspólne dla obu widoków (galeria pełna / edytor z szyną kontekstu):
  // modal nazwy (create/duplicate/rename), confirm usunięcia z galerii, podgląd
  // menu modułowego. Renderowane raz, żeby akcje szyny działały też w edytorze.
  const sharedModals = (
    <>
      {menuTransfer ? (
        <menuTransfer.ImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={(layoutId) => void handleMenuImported(layoutId)}
          onPreview={setImportPreviewMenu}
        />
      ) : null}
      <LayoutNameModal
        open={nameModalMode !== null}
        mode={nameModalMode ?? 'create'}
        currentName={duplicateSourceName ?? modalSourceLayout?.name}
        currentIcon={modalIcon}
        currentColor={modalColor}
        currentActive={modalSourceLayout?.isActive !== false}
        activeLocked={nameModalMode === 'rename' && !!modalSourceLayout?.isDefault}
        onSubmit={handleNameSubmit}
        onClose={closeNameModal}
        serverError={nameModalServerError}
        submitting={
          createMutation.isPending || renameMutation.isPending || duplicateModuleMutation.isPending
        }
        i18nPrefix={i18nPrefix}
      />
      <ConfirmDialog
        open={galleryDeleteId !== null}
        title={t(`${i18nPrefix}.layouts.confirmDelete.title`)}
        message={t(`${i18nPrefix}.layouts.confirmDelete.message`, {
          name: layouts.find((l) => l.id === galleryDeleteId)?.name ?? '',
        })}
        confirmLabel={t(`${i18nPrefix}.layouts.confirmDelete.confirm`)}
        variant="danger"
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (galleryDeleteId) deleteMutation.mutate(galleryDeleteId);
        }}
        onClose={() => {
          if (!deleteMutation.isPending) setGalleryDeleteId(null);
        }}
      />
      <ConfirmDialog
        open={pendingModuleCloneConfirm !== null}
        title={t(`${i18nPrefix}.layouts.confirmSwitch.title`)}
        message={t(`${i18nPrefix}.layouts.confirmSwitch.message`)}
        confirmLabel={t(`${i18nPrefix}.layouts.confirmSwitch.confirm`)}
        variant="danger"
        onConfirm={() => {
          if (pendingModuleCloneConfirm) {
            setPendingModuleClone(pendingModuleCloneConfirm);
            setPendingModuleCloneConfirm(null);
            openNameModal('duplicate');
          }
        }}
        onClose={() => setPendingModuleCloneConfirm(null)}
      />
      {previewModuleMenu ? (
        resolvePreviewNode ? (
          // Pełny zunifikowany podgląd (tenant): klikalna makieta + prawy panel
          // lista/formularz na próbkach — jak podgląd menu w hubie.
          <MenuUnifiedPreview
            open
            onClose={closeMenuPreview}
            hierarchy={previewModuleMenu.hierarchy}
            modules={sourceModules}
            moduleName={previewModuleMenu.name}
            title={t(`${i18nPrefix}.gallery.previewModuleMenuTitle`, {
              name: previewModuleMenu.name,
              defaultValue: `Podgląd menu — ${previewModuleMenu.name}`,
            })}
            resolveNode={resolvePreviewNode}
          />
        ) : (
          <PreviewModal
            open
            onClose={closeMenuPreview}
            title={t(`${i18nPrefix}.gallery.previewModuleMenuTitle`, {
              name: previewModuleMenu.name,
              defaultValue: `Podgląd menu — ${previewModuleMenu.name}`,
            })}
          >
            <div className="flex h-full min-h-0">
              <div className="w-72 flex-none overflow-y-auto">
                <MenuPreviewSidebar
                  hierarchy={previewModuleMenu.hierarchy}
                  modules={[]}
                  moduleName={previewModuleMenu.name}
                  hideNote
                  showPinnedBase
                  fullHeight
                />
              </div>
            </div>
          </PreviewModal>
        )
      ) : null}
    </>
  );

  // ── Akordeon pozycji (tryb 'list' + kartotekaViews) ────────────────────────
  // Przypisanie widoku pozycji: viewRef/listRef 'kartotekaSlug:layoutId' na
  // ZAZNACZONEJ pozycji (null = domyślny → klucz znika). Zapis idzie z hierarchią.
  const assignRefToSelected = (key: 'viewRef' | 'listRef', ref: string | null) => {
    if (!selectedId) return;
    setHierarchy((h) =>
      updateInTree(h as MenuItem[], selectedId, (n) => {
        const next = { ...n } as MenuItem;
        if (ref) next[key] = ref;
        else delete next[key];
        return next;
      }),
    );
  };

  // Chipy widoków na wierszu pozycji („Formularz: X | Lista: Y") — jak w hubie.
  const resolveTenantViewLabels = (item: MenuItem) => {
    if (!kartotekaViews) return null;
    const slug = kartotekaViews.resolveEntitySlug(item);
    if (!slug) return null;
    const entity = kartotekaViews.entities.find((e) => e.slug === slug);
    if (!entity) return null;
    const defaultName = t('admin.menuItemViews.defaultLayout', { defaultValue: 'Domyślny' });
    const pickName = (ref: string | undefined, type: 'form' | 'list') => {
      if (!ref) return defaultName;
      const sep = ref.indexOf(':');
      const viewSlug = sep > 0 ? ref.slice(sep + 1) : null;
      const view = viewSlug
        ? entity.views.find((v) => v.type === type && v.slug === viewSlug)
        : undefined;
      return view?.name ?? defaultName;
    };
    return {
      form: t('admin.menuLayoutBuilder.viewChips.form', {
        name: pickName(item.viewRef, 'form'),
        defaultValue: `Formularz: ${pickName(item.viewRef, 'form')}`,
      }),
      list: t('admin.menuLayoutBuilder.viewChips.list', {
        name: pickName(item.listRef, 'list'),
        defaultValue: `Lista: ${pickName(item.listRef, 'list')}`,
      }),
    };
  };

  const useInlineAccordion = paneMode === 'list' && !!kartotekaViews;

  // ── Mutacje layoutów kartotek z akordeonu (etap b cz.2) ────────────────────
  // Callbacki edytora są fire-and-forget (zwrotka ignorowana) — wiersz pojawia się
  // po invalidacji źródła kartoteki. Błąd (np. NAZWA zajęta na backendzie) → toast.
  const selectedKartotekaSlug =
    useInlineAccordion && kartotekaViews && selectedItem
      ? kartotekaViews.resolveEntitySlug(selectedItem)
      : null;
  const canMutateSelectedKartoteka = !!(
    selectedKartotekaSlug && kartotekaLayoutActions?.canMutate(selectedKartotekaSlug)
  );

  // Zwraca obietnicę (po obsłużeniu błędu → nigdy nie rzuca), żeby wołający
  // (MenuItemInlineEditor) mógł ją zaczekać i pokazać spinner „Tworzę…" do końca
  // POST-a + refetchu definicji layoutów.
  const runLayoutAction = (p: Promise<unknown>) =>
    p.catch((e: { response?: { data?: { error?: string } } }) => {
      const code = e.response?.data?.error;
      toast.show({
        variant: 'error',
        title:
          code === 'VALIDATION_ERROR'
            ? t('admin.menuItemViews.duplicateName', {
                defaultValue: 'Widok o tej nazwie już istnieje.',
              })
            : t('admin.menuItemViews.layoutActionError', {
                defaultValue: 'Nie udało się zapisać zmiany layoutu.',
              }),
      });
    });

  // Usunięcie layoutu: po sukcesie zdejmij osierocone viewRef/listRef z CAŁEJ
  // roboczej hierarchii (pozycja wraca na „Domyślny"; zmiana wchodzi z zapisem menu).
  const stripRefInTree = (
    items: Array<MenuItem | AdminMenuItem>,
    ref: string,
  ): Array<MenuItem | AdminMenuItem> =>
    items.map((it) => {
      const next = { ...it } as MenuItem;
      if (next.viewRef === ref) delete next.viewRef;
      if (next.listRef === ref) delete next.listRef;
      if (next.children) next.children = stripRefInTree(next.children, ref) as MenuItem[];
      return next;
    });

  const handleDeleteKartotekaLayout = (slug: string, layoutId: string) => {
    if (!kartotekaLayoutActions) return;
    runLayoutAction(
      kartotekaLayoutActions.deleteLayout(slug, layoutId).then(() => {
        setHierarchy((h) => stripRefInTree(h, `${slug}:${layoutId}`));
      }),
    );
  };

  // Modal buildera formularza z akordeonu („Przeglądaj/edytuj" nazwanego layoutu
  // albo „Podgląd" domyślnego — readOnly). layoutId=null → domyślny.
  const [formBuilderTarget, setFormBuilderTarget] = useState<{
    slug: string;
    layoutId: string | null;
    readOnly: boolean;
  } | null>(null);
  const formBuilderData =
    formBuilderTarget && kartotekaLayoutActions
      ? kartotekaLayoutActions.getFormBuilderData(
          formBuilderTarget.slug,
          formBuilderTarget.layoutId,
        )
      : null;

  // Modal buildera WIDOKU LISTY z akordeonu (2026-07-03) — natywna persystencja
  // paneli (PUT z useTableLayout, sufiks forcedLayoutId celuje w kliknięty layout);
  // po zamknięciu invalidacja źródeł kartoteki (wiersze/chipy/podgląd).
  const [listBuilderTarget, setListBuilderTarget] = useState<{
    slug: string;
    layoutId: string | null;
    readOnly: boolean;
  } | null>(null);
  const listBuilderData =
    listBuilderTarget && kartotekaLayoutActions
      ? kartotekaLayoutActions.getListBuilderTarget(
          listBuilderTarget.slug,
          listBuilderTarget.layoutId,
        )
      : null;
  const closeListBuilder = () => {
    const slug = listBuilderTarget?.slug;
    setListBuilderTarget(null);
    if (slug && kartotekaLayoutActions) void kartotekaLayoutActions.invalidateKartoteka(slug);
  };

  // Trzy kolumny edytora — wspólne dla trybu 'gallery' (edytor pełnoekranowy)
  // i 'list' (edytor w prawej kolumnie). Tryb 'list' z kartotekaViews: bez prawego
  // panelu właściwości — edycja w akordeonie pod zaznaczonym wierszem (jak w hubie).
  const editorColumns = (
    <div className="flex flex-1 overflow-hidden">
      {/* Paleta zwijalna + poszerzalna (tenant i admin). Otwarta = kolumna z ikoną
          zwijania (X) + uchwyt do poszerzania; zwinięta = wąski pasek z ikoną rozwijania.
          Mode-agnostyczne: pasek rozwijania działa i w trybie list (tenant), i galerii
          (admin), bez zależności od nagłówka danego trybu. */}
      {paletteOpen ? (
        <>
          <MenuPalette
            source={sourceQuery.data}
            isLoading={sourceQuery.isLoading}
            loadError={
              sourceQuery.isError
                ? isAxiosError(sourceQuery.error) && sourceQuery.error.response?.status === 403
                  ? 'forbidden'
                  : 'failed'
                : null
            }
            onAdd={handleAddItem}
            i18nPrefix={i18nPrefix}
            allowModuleType={allowModuleType}
            onCreateKartoteka={onCreateKartoteka}
            widthPx={paletteWidth}
            onCollapse={() => setPaletteOpen(false)}
            enableDrag={enablePaletteDnd}
            usedKeys={usedPaletteKeys}
          />
          <ColumnResizer width={paletteWidth} onWidthChange={setPaletteWidth} min={200} max={560} />
        </>
      ) : (
        <CollapsedColumnRail
          label={t(`${i18nPrefix}.palette.header`, { defaultValue: 'Paleta' })}
          ariaLabel={t(`${i18nPrefix}.showPalette`, { defaultValue: 'Pokaż paletę' })}
          onExpand={() => setPaletteOpen(true)}
          testId="menu-palette-expand"
        />
      )}
      <MenuCanvas
        hierarchy={hierarchy as MenuItem[]}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onDeselect={useInlineAccordion ? () => setSelectedId(null) : undefined}
        onDelete={handleDeleteItem}
        onReorder={handleReorder}
        onMove={handleMove}
        enableDrag={enablePaletteDnd}
        onAddAt={handleAddItemAt}
        i18nPrefix={i18nPrefix}
        systemItemDefinitions={systemItemDefinitions}
        resolveViewLabels={useInlineAccordion ? resolveTenantViewLabels : undefined}
        onChangeItem={useInlineAccordion ? (next) => handleUpdateItem(next) : undefined}
        inlineEditorProps={
          useInlineAccordion && kartotekaViews
            ? {
                entities: kartotekaViews.entities,
                resolveEntitySlug: kartotekaViews.resolveEntitySlug,
                // Ta gałąź to ścieżka TENANTA: `kartotekaViews` to syntetyczne encje z kartotek
                // tenanta (useTenantKartotekaViews). Picker typów dokumentów w filtrach stałych ma więc
                // czytać scaloną listę tenanta, nie katalog globalny ITE.
                documentTypePickerScope: 'tenant' as const,
                onAssignForm: (viewRef) => assignRefToSelected('viewRef', viewRef),
                onAssignList: (listRef) => assignRefToSelected('listRef', listRef),
                // „Podgląd na żywo" per sekcja USUNIĘTY (feedback Eugenii 2026-07-03)
                // — od etapu b cz.2 layouty edytuje się realnie; podgląd całości
                // zostaje pod „Podgląd menu" w nagłówku edytora.
                // Mutacje layoutów (etap b cz.2) — tylko gdy tenant podał akcje
                // i user ma uprawnienie manage dla rodzaju zaznaczonej kartoteki.
                ...(canMutateSelectedKartoteka && selectedKartotekaSlug && kartotekaLayoutActions
                  ? {
                      onCreateForm: (name: string) =>
                        runLayoutAction(
                          kartotekaLayoutActions.createLayout(selectedKartotekaSlug, 'form', name),
                        ),
                      onDuplicateForm: (sourceSlug: string | null, name: string) =>
                        runLayoutAction(
                          kartotekaLayoutActions.duplicateLayout(
                            selectedKartotekaSlug,
                            'form',
                            sourceSlug,
                            name,
                          ),
                        ),
                      onDeleteForm: (layoutId: string) =>
                        handleDeleteKartotekaLayout(selectedKartotekaSlug, layoutId),
                      onCreateList: (name: string) =>
                        runLayoutAction(
                          kartotekaLayoutActions.createLayout(selectedKartotekaSlug, 'list', name),
                        ),
                      onDuplicateList: (sourceSlug: string | null, name: string) =>
                        runLayoutAction(
                          kartotekaLayoutActions.duplicateLayout(
                            selectedKartotekaSlug,
                            'list',
                            sourceSlug,
                            name,
                          ),
                        ),
                      onDeleteList: (layoutId: string) =>
                        handleDeleteKartotekaLayout(selectedKartotekaSlug, layoutId),
                      // Inline rename z ołówka na wierszu (2026-07-03) — walidacja
                      // unikalności w edytorze, backend też waliduje (VALIDATION_ERROR → toast).
                      onRenameForm: (layoutId: string, name: string) =>
                        runLayoutAction(
                          kartotekaLayoutActions.renameLayout(
                            selectedKartotekaSlug,
                            layoutId,
                            name,
                          ),
                        ),
                      onRenameList: (layoutId: string, name: string) =>
                        runLayoutAction(
                          kartotekaLayoutActions.renameLayout(
                            selectedKartotekaSlug,
                            layoutId,
                            name,
                          ),
                        ),
                      // „Przeglądaj/edytuj" nazwanego layoutu formularza + „Podgląd"
                      // domyślnego (read-only) — modal buildera (etap b cz.2).
                      onDesignForm: (layoutId: string | null) =>
                        setFormBuilderTarget({
                          slug: selectedKartotekaSlug,
                          layoutId,
                          readOnly: false,
                        }),
                      onPreviewForm: (layoutId: string | null) =>
                        setFormBuilderTarget({
                          slug: selectedKartotekaSlug,
                          layoutId,
                          readOnly: true,
                        }),
                      // Builder widoku listy (2026-07-03) — edycja nazwanego +
                      // podgląd domyślnego, jak dla formularzy.
                      onDesignList: (layoutId: string | null) =>
                        setListBuilderTarget({
                          slug: selectedKartotekaSlug,
                          layoutId,
                          readOnly: false,
                        }),
                      onPreviewList: (layoutId: string | null) =>
                        setListBuilderTarget({
                          slug: selectedKartotekaSlug,
                          layoutId,
                          readOnly: true,
                        }),
                    }
                  : {}),
              }
            : undefined
        }
      />
      {useInlineAccordion ? null : (
        <MenuItemPropertiesPanel
          item={selectedItem}
          onChange={handleUpdateItem}
          i18nPrefix={i18nPrefix}
        />
      )}
    </div>
  );

  // Dialogi wspólne edytora (oba tryby): confirm „Wstecz" przy dirty, confirm
  // przełączenia layoutu przy dirty, modal przypisań ról.
  const commonEditorDialogs = (
    <>
      <ConfirmDialog
        open={confirmBack}
        title={t(`${i18nPrefix}.confirmBack.title`)}
        message={t(`${i18nPrefix}.confirmBack.message`)}
        confirmLabel={t(`${i18nPrefix}.confirmBack.confirm`)}
        variant="danger"
        onConfirm={confirmBackAction}
        onClose={() => setConfirmBack(false)}
      />
      <ConfirmDialog
        open={pendingSwitchTo !== null}
        title={t(`${i18nPrefix}.layouts.confirmSwitch.title`)}
        message={t(`${i18nPrefix}.layouts.confirmSwitch.message`)}
        confirmLabel={t(`${i18nPrefix}.layouts.confirmSwitch.confirm`)}
        variant="danger"
        onConfirm={confirmSwitch}
        onClose={() => setPendingSwitchTo(null)}
      />
      {roleAssignmentsEnabled && rolesQuery && (
        <LayoutRolesModal
          open={rolesModalOpen}
          layoutName={currentLayout?.name ?? ''}
          expectedVersion={currentVersion}
          onSubmit={(roleIds) => updateRolesMutation.mutate(roleIds)}
          onClose={() => {
            setRolesModalOpen(false);
            setRolesModalServerError(null);
          }}
          submitting={updateRolesMutation.isPending}
          serverError={rolesModalServerError}
          i18nPrefix={i18nPrefix}
          rolesQuery={rolesQuery}
          assignmentsQuery={assignmentsQuery}
        />
      )}
    </>
  );

  // ───── tryb 'list' (tenant) — jeden widok: lista po lewej + edytor po prawej ────
  if (paneMode === 'list') {
    const editorOpen = view === 'editor' && currentLayoutId !== null;
    // Ukrywamy tylko seedowany pusty placeholder „Default" (isEmpty && isDefault) — NIE
    // wszystkie puste menu. Menu customowe użytkownika jest puste do pierwszego zapisu
    // pozycji, a musi zostać widoczne po odświeżeniu (inaczej znika z listy, choć istnieje
    // w bazie i blokuje swoją nazwę). Wyjątek: menu OTWARTE w edytorze zawsze widoczne.
    const visibleLayouts = layouts.filter(
      (l) =>
        !(l.isEmpty === true && l.isDefault === true) || (editorOpen && l.id === currentLayoutId),
    );
    return (
      <div
        className={`flex ${heightClassName} flex-col overflow-hidden rounded-2xl border border-border-secondary bg-bg-secondary`}
        data-testid="menu-builder-page"
      >
        <header className="flex items-center justify-between border-b border-border-secondary bg-bg-elevated px-4 py-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
              data-testid="menu-builder-back"
            >
              <ArrowLeft className="h-4 w-4" />
              {t(`${i18nPrefix}.back`)}
            </button>
            <h1 className="text-lg font-semibold text-text-primary">{t(`${i18nPrefix}.title`)}</h1>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {menuListOpen ? (
            <>
              <MenuListPane
                moduleMenus={moduleMenus ?? []}
                layouts={visibleLayouts}
                isLoading={layoutsQuery.isLoading}
                openLayoutId={editorOpen ? currentLayoutId : null}
                onSystemPreview={(code) => setPreviewModuleCode(code)}
                onSystemDuplicate={handleModuleMenuDuplicate}
                onAddCustom={() => openNameModal('create')}
                onOpenCustom={openCustomLayout}
                onDuplicateCustom={(id) => {
                  setGalleryCloneFromId(id);
                  openNameModal('duplicate');
                }}
                onEditCustom={handleEditCustom}
                onDeleteCustom={(id) => setGalleryDeleteId(id)}
                onExportCustom={
                  menuTransfer
                    ? (id) => {
                        // Eksport bierze wersję ZAPISANĄ — przy niezapisanych zmianach uprzedzamy.
                        if (editorOpen && id === currentLayoutId && isDirty) {
                          toast.show({
                            variant: 'warning',
                            title: t(`${i18nPrefix}.export.unsavedHint`),
                          });
                        }
                        menuTransfer.onExport(id);
                      }
                    : undefined
                }
                onImportCustom={menuTransfer ? () => setImportOpen(true) : undefined}
                i18nPrefix={i18nPrefix}
                widthPx={menuListWidth}
                onCollapse={() => setMenuListOpen(false)}
              />
              <ColumnResizer
                width={menuListWidth}
                onWidthChange={setMenuListWidth}
                min={220}
                max={560}
              />
            </>
          ) : (
            <CollapsedColumnRail
              label={t(`${i18nPrefix}.listPane.railLabel`, { defaultValue: 'Menu' })}
              ariaLabel={t(`${i18nPrefix}.showMenuList`, { defaultValue: 'Pokaż listę menu' })}
              onExpand={() => setMenuListOpen(true)}
              testId="menu-list-expand"
            />
          )}

          {editorOpen ? (
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              {/* Nagłówek kolumny edytora — nazwa + akcje otwartego menu */}
              {/* Jedna linia: długa nazwa skracana wielokropkiem — nagłówek nie zmienia wysokości
                  przy przełączaniu menu (inaczej cały edytor pod nim podskakiwał). */}
              <div className="flex items-center justify-between gap-2 border-b border-border-secondary bg-bg-elevated px-4 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="min-w-0 truncate text-sm font-semibold text-text-primary"
                    title={currentName || currentLayout?.name || undefined}
                    data-testid="menu-editor-current-name"
                  >
                    {currentName || currentLayout?.name || '—'}
                  </span>
                  {isCurrentDefault ? (
                    <span className="shrink-0 whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      {t(`${i18nPrefix}.layouts.isDefaultBadge`)}
                    </span>
                  ) : null}
                  {isDirty ? (
                    <span className="shrink-0 whitespace-nowrap rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      {t(`${i18nPrefix}.dirty`)}
                    </span>
                  ) : null}
                  {activeControls}
                  <button
                    type="button"
                    onClick={() => setDefaultMutation.mutate()}
                    disabled={isCurrentDefault || setDefaultMutation.isPending}
                    className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
                    data-testid="layout-action-set-default"
                    title={t(`${i18nPrefix}.layouts.actions.setDefault`)}
                  >
                    <Star className="h-3.5 w-3.5" />
                  </button>
                  {roleAssignmentsEnabled && (
                    <button
                      type="button"
                      onClick={() => {
                        setRolesModalServerError(null);
                        setRolesModalOpen(true);
                      }}
                      className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
                      data-testid="layout-action-roles"
                      title={t(`${i18nPrefix}.layouts.actions.roleAssignments`)}
                    >
                      <Users className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewCurrentOpen(true)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
                    data-testid="menu-builder-preview"
                  >
                    <Eye className="h-4 w-4" />
                    {t(`${i18nPrefix}.listPane.previewButton`, { defaultValue: 'Podgląd menu' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => saveMutation.mutate()}
                    disabled={!isDirty || saveMutation.isPending}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-sm font-medium text-white transition disabled:opacity-50 enabled:hover:bg-accent-600"
                    data-testid="menu-builder-save"
                  >
                    <Save className="h-4 w-4" />
                    {saveMutation.isPending ? t(`${i18nPrefix}.saving`) : t(`${i18nPrefix}.save`)}
                  </button>
                </div>
              </div>
              {editorColumns}
            </div>
          ) : (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-3 p-12 text-center"
              data-testid="menu-builder-placeholder"
            >
              <h2 className="text-lg font-semibold text-text-primary">
                {t(`${i18nPrefix}.listPane.placeholderTitle`, {
                  defaultValue: 'Wybierz menu do edycji',
                })}
              </h2>
              <p className="max-w-md text-sm text-text-secondary">
                {t(`${i18nPrefix}.listPane.placeholderBody`, {
                  defaultValue:
                    'Kliknij menu customowe z listy po lewej albo utwórz nowe plusem. Menu systemowe możesz podejrzeć i zduplikować.',
                })}
              </p>
            </div>
          )}
        </div>

        {sharedModals}
        {commonEditorDialogs}

        {/* Podgląd roboczej hierarchii otwartego menu (przycisk „Podgląd menu") */}
        {previewCurrentOpen && currentLayoutId ? (
          resolvePreviewNode ? (
            <MenuUnifiedPreview
              open
              onClose={() => {
                setPreviewCurrentOpen(false);
                setPreviewCurrentNodeId(null);
                setPreviewCurrentMode('list');
              }}
              hierarchy={hierarchy as MenuItem[]}
              modules={sourceModules}
              moduleName={currentName || currentLayout?.name || ''}
              initialNodeId={previewCurrentNodeId}
              initialMode={previewCurrentMode}
              title={t(`${i18nPrefix}.gallery.previewModuleMenuTitle`, {
                name: currentName || currentLayout?.name || '',
                defaultValue: `Podgląd menu — ${currentName || currentLayout?.name || ''}`,
              })}
              resolveNode={resolvePreviewNode}
            />
          ) : (
            <PreviewModal
              open
              onClose={() => setPreviewCurrentOpen(false)}
              title={t(`${i18nPrefix}.gallery.previewModuleMenuTitle`, {
                name: currentName || currentLayout?.name || '',
                defaultValue: `Podgląd menu — ${currentName || currentLayout?.name || ''}`,
              })}
            >
              <div className="flex h-full min-h-0">
                <div className="w-72 flex-none overflow-y-auto">
                  <MenuPreviewSidebar
                    hierarchy={hierarchy as MenuItem[]}
                    modules={[]}
                    moduleName={currentName || currentLayout?.name || ''}
                    hideNote
                    showPinnedBase
                    fullHeight
                  />
                </div>
              </div>
            </PreviewModal>
          )
        ) : null}

        {/* Builder layoutu formularza kartoteki z akordeonu (etap b cz.2) */}
        {formBuilderTarget && formBuilderData && kartotekaLayoutActions ? (
          <TenantKartotekaFormBuilderModal
            entitySlug={formBuilderTarget.slug}
            data={formBuilderData}
            readOnly={formBuilderTarget.readOnly}
            onClose={() => setFormBuilderTarget(null)}
            onSave={async (tree) => {
              if (!formBuilderTarget.layoutId) return;
              try {
                await kartotekaLayoutActions.saveFormLayout(
                  formBuilderTarget.slug,
                  formBuilderTarget.layoutId,
                  tree,
                );
                toast.show({
                  variant: 'success',
                  title: t('admin.menuItemViews.layoutSaved', {
                    defaultValue: 'Layout zapisany.',
                  }),
                });
              } catch (err) {
                // T20D — LAYOUT_CONFIG_INVALID (np. layout bez pola) niesie konkretny
                // komunikat w issues[]; inne błędy dostają generyczny fallback.
                const issues = isAxiosError(err)
                  ? (err.response?.data as { issues?: unknown } | undefined)?.issues
                  : null;
                const description =
                  Array.isArray(issues) && issues.length > 0
                    ? issues.map(String).join('\n')
                    : undefined;
                toast.show({
                  variant: 'error',
                  title: t('admin.menuItemViews.layoutActionError', {
                    defaultValue: 'Nie udało się zapisać zmiany layoutu.',
                  }),
                  description,
                });
                // Rethrow — `TenantKartotekaFormBuilderModal.handleBuilderSave` czeka na
                // odrzucenie, żeby NIE zamknąć canvasu przy błędzie (patrz komponent:
                // `onClose()` leci dopiero po udanym `await onSave(...)`). Bez tego modal
                // zamykał się cicho przy KAŻDYM błędzie zapisu — użytkownik tracił
                // niezapisane zmiany bez żadnej informacji (zgłoszone w smoke teście,
                // 2026-08-26 — pusty layout formularza).
                throw err;
              }
            }}
          />
        ) : null}

        {/* Builder widoku listy kartoteki z akordeonu (2026-07-03) */}
        {listBuilderTarget && listBuilderData ? (
          <TenantKartotekaListBuilderModal
            scope={listBuilderData.scope}
            code={listBuilderData.code}
            forcedLayoutId={listBuilderData.forcedLayoutId}
            entityName={listBuilderData.entityName}
            layoutName={listBuilderData.layoutName}
            readOnly={listBuilderTarget.readOnly}
            onClose={closeListBuilder}
          />
        ) : null}
      </div>
    );
  }

  // ───── tryb 'gallery' (admin/ITE) — galeria pełnoekranowa ↔ edytor ──────────
  if (view === 'gallery') {
    return (
      <>
        <MenuLayoutGallery
          layouts={layouts}
          isLoading={layoutsQuery.isLoading}
          view={galleryView}
          onViewChange={changeGalleryView}
          onOpen={openLayout}
          onNew={() => openNameModal('create')}
          onBack={() => navigate(backUrl)}
          i18nPrefix={i18nPrefix}
          onDuplicate={(id) => {
            setGalleryCloneFromId(id);
            openNameModal('duplicate');
          }}
          onDelete={(id) => setGalleryDeleteId(id)}
          moduleMenus={moduleMenus}
          onModuleMenuPreview={moduleMenus ? (code) => setPreviewModuleCode(code) : undefined}
          onModuleMenuDuplicate={moduleMenus ? handleModuleMenuDuplicate : undefined}
          kartoteki={galleryKartoteki}
          heightClassName={heightClassName}
        />
        {sharedModals}
      </>
    );
  }

  return (
    <div
      className={`flex ${heightClassName} flex-col overflow-hidden rounded-2xl border border-border-secondary bg-bg-secondary`}
      data-testid="menu-builder-page"
    >
      {/* Toolbar */}
      <header className="flex items-center justify-between border-b border-border-secondary bg-bg-elevated px-4 py-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
            data-testid="menu-builder-back"
          >
            <ArrowLeft className="h-4 w-4" />
            {t(`${i18nPrefix}.back`)}
          </button>
          <h1 className="text-lg font-semibold text-text-primary">{t(`${i18nPrefix}.title`)}</h1>

          {/* Layout selector */}
          {hasLayouts ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setLayoutDropdownOpen((o) => !o)}
                className="flex items-center gap-2 rounded-md border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
                data-testid="layout-selector-toggle"
              >
                <span className="font-medium">{currentName || currentLayout?.name || '—'}</span>
                {isCurrentDefault ? (
                  <span
                    className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                    data-testid="layout-selector-default-badge"
                  >
                    {t(`${i18nPrefix}.layouts.isDefaultBadge`)}
                  </span>
                ) : null}
                <ChevronDown className="h-4 w-4 text-text-tertiary" />
              </button>
              {layoutDropdownOpen ? (
                <div
                  className="absolute left-0 top-full z-30 mt-1 w-64 rounded-md border border-border-secondary bg-bg-elevated py-1 shadow-lg"
                  data-testid="layout-selector-dropdown"
                >
                  {layouts.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => handleSwitchLayout(l.id)}
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-text-primary hover:bg-bg-secondary"
                      data-testid={`layout-selector-item-${l.id}`}
                    >
                      <span className="flex items-center gap-2">
                        {l.id === currentLayoutId ? (
                          <Check className="h-3.5 w-3.5 text-accent-600" />
                        ) : (
                          <span className="w-3.5" />
                        )}
                        <span>{l.name}</span>
                      </span>
                      {l.isDefault ? (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                          {t(`${i18nPrefix}.layouts.isDefaultBadge`)}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => openNameModal('create')}
              className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
              data-testid="layout-action-new"
              title={t(`${i18nPrefix}.layouts.actions.new`)}
            >
              <Plus className="h-3.5 w-3.5" />
              {t(`${i18nPrefix}.layouts.actions.new`)}
            </button>
            {currentLayoutId ? (
              <>
                <button
                  type="button"
                  onClick={() => openNameModal('duplicate')}
                  className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
                  data-testid="layout-action-duplicate"
                  title={t(`${i18nPrefix}.layouts.actions.duplicate`)}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => openNameModal('rename')}
                  className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
                  data-testid="layout-action-rename"
                  title={t(`${i18nPrefix}.layouts.actions.rename`)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setDefaultMutation.mutate()}
                  disabled={isCurrentDefault || setDefaultMutation.isPending}
                  className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
                  data-testid="layout-action-set-default"
                  title={t(`${i18nPrefix}.layouts.actions.setDefault`)}
                >
                  <Star className="h-3.5 w-3.5" />
                </button>
                {activeControls}
                {roleAssignmentsEnabled && (
                  <button
                    type="button"
                    onClick={() => {
                      setRolesModalServerError(null);
                      setRolesModalOpen(true);
                    }}
                    className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
                    data-testid="layout-action-roles"
                    title={t(`${i18nPrefix}.layouts.actions.roleAssignments`)}
                  >
                    <Users className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm text-red-600 hover:bg-red-50"
                  data-testid="layout-action-delete"
                  title={t(`${i18nPrefix}.layouts.actions.delete`)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            ) : null}
          </div>

          {isDirty ? (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              {t(`${i18nPrefix}.dirty`)}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!currentLayoutId || !isDirty || saveMutation.isPending}
            className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-sm font-medium text-white transition disabled:opacity-50 enabled:hover:bg-accent-600"
            data-testid="menu-builder-save"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? t(`${i18nPrefix}.saving`) : t(`${i18nPrefix}.save`)}
          </button>
        </div>
      </header>

      {/* Body — 3 columns lub empty-state */}
      {!hasLayouts && !layoutsQuery.isLoading ? (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-3 p-12 text-center"
          data-testid="menu-builder-empty"
        >
          <h2 className="text-lg font-semibold text-text-primary">
            {t(`${i18nPrefix}.layouts.empty.title`)}
          </h2>
          <p className="max-w-md text-sm text-text-secondary">
            {t(`${i18nPrefix}.layouts.empty.body`)}
          </p>
          <button
            type="button"
            onClick={() => openNameModal('create')}
            className="flex items-center gap-2 rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
            data-testid="menu-builder-empty-new"
          >
            <Plus className="h-4 w-4" />
            {t(`${i18nPrefix}.layouts.actions.new`)}
          </button>
        </div>
      ) : (
        editorColumns
      )}

      {/* Modale wspólne z galerią: modal nazwy (create/duplicate/rename), confirm
          usunięcia z galerii, confirm duplikatu przy dirty, podgląd menu modułowego. */}
      {sharedModals}

      {/* Confirm back / confirm switch / role assignments */}
      {commonEditorDialogs}

      {/* Confirm delete */}
      <ConfirmDialog
        open={confirmDelete}
        title={t(`${i18nPrefix}.layouts.confirmDelete.title`)}
        message={t(`${i18nPrefix}.layouts.confirmDelete.message`, {
          name: currentLayout?.name ?? '',
        })}
        confirmLabel={t(`${i18nPrefix}.layouts.confirmDelete.confirm`)}
        variant="danger"
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (currentLayoutId) deleteMutation.mutate(currentLayoutId);
        }}
        onClose={() => {
          if (!deleteMutation.isPending) setConfirmDelete(false);
        }}
      />
    </div>
  );
}
